import { Flex, Group, Select, Stack } from '@mantine/core';
import { DatePickerInput, type DatesRangeValue } from '@mantine/dates';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { useDeliveryImpact } from '../../../api/agent-miner-client/agent-miner-client.hooks';
import { ChartBox } from '../../../components/chart-styles/chart-styles';
import { FilterPanel } from '../../../components/filters/filter-panel';
import { formatDateRange, STANDARD_DATE_FORMATS } from '../../../helpers/date-time-helpers';
import { useAgentMinerStore } from '../../../store/agent-miner-store/agent-miner-store';
import { inkLight } from '../../../styles/design-tokens';
import { Divider } from '../../../ui-library/divider/divider';
import { Icon } from '../../../ui-library/icon/icon';
import { H3, Text } from '../../../ui-library/typography/typography';
import type { TimeAllocationType } from '../../../api/work-periods-client/work-periods-client.type';
import { SESSIONS_PANEL_PAGE_SIZE } from '../agent-miner.constants';
import { AgentMinerTab } from '../agent-miner.type';
import { MODEL_COMPARISON_ALLOCATION_TYPES } from '../model-comparison/model-comparison.helpers';
import { useModelComparisonAllocationType } from '../model-comparison/model-comparison.hooks';
import { ALLOCATION_TYPE_LABELS } from '../model-comparison/model-comparison.type';
import { SessionListSideSection } from '../shared/session-list-panel/session-list-side-section';
import { toDateOrNull, toIsoDateOrNull } from '../sessions-list/filters/session-filters.helpers';
import {
  DATE_OPTIONS,
  type DateRangePreset,
  RESET_KEYS,
  type SessionsFiltersValue,
} from '../sessions-list/filters/session-filters.type';
import { ComparisonCards } from './comparison-cards';
import { isDeliveryImpactEmpty } from './delivery-impact.helpers';
import { useDeliveryImpactDrilldown, useDeliveryImpactParams, useDeliveryImpactView } from './delivery-impact.hooks';
import { DeliveryTrendChart } from './delivery-trend-chart';

const DEFAULT_DATE_PRESET: DateRangePreset = '30d';

// Delivery Impact is window-scoped, so "All time" isn't offered on this tab.
const DELIVERY_IMPACT_DATE_OPTIONS = DATE_OPTIONS.filter((option) => option.value !== 'all');

type DeliveryImpactFiltersProps = {
  value: SessionsFiltersValue;
  setDateRange: (value: DateRangePreset) => void;
  setCustomRange: (start: string | null, end: string | null) => void;
  onClose: () => void;
};

/**
 * Side filter panel for the Delivery Impact tab: a tiles/trend view selector
 * at the top and the same date-window and allocation controls the Model
 * Comparison tab uses (shared URL params, so the range carries across tabs).
 *
 * @param {DeliveryImpactFiltersProps} props - Component props
 * @returns {JSX.Element} The filter panel content
 */
export const DeliveryImpactFilters = ({ value, setDateRange, setCustomRange, onClose }: DeliveryImpactFiltersProps) => {
  const { t } = useTranslation();
  const { view, setView } = useDeliveryImpactView();
  const { effectiveAllocationType, setAllocationType } = useModelComparisonAllocationType();
  // Own search-params instance for Reset — the individual URL-backed setters
  // each snapshot searchParams at call time, so composing them in one handler
  // races off the same stale snapshot (see ModelComparisonFilters).
  const [searchParams, setSearchParams] = useSearchParams();

  const handleResetFilters = () => {
    const next = new URLSearchParams(searchParams);
    for (const key of RESET_KEYS) next.delete(key);
    next.delete('di_view');
    next.delete('di_panel');
    next.delete('di_chart_view');
    next.delete('allocation');
    next.set('page', '1');
    setSearchParams(next, { replace: true });
  };

  const dateSection = (
    <Stack gap={16}>
      <Group gap="xs">
        <Icon name="schedule" size={16} color={inkLight} />
        <Text weight="medium" size="small">
          {t('agent_miner.started')}
        </Text>
      </Group>
      <Select
        data={DELIVERY_IMPACT_DATE_OPTIONS.map((option) => ({ value: option.value, label: option.label }))}
        // A stale "All time" from the shared Sessions param shows as the default.
        value={value.dateRange === 'all' ? DEFAULT_DATE_PRESET : value.dateRange}
        onChange={(next) => setDateRange((next ?? DEFAULT_DATE_PRESET) as DateRangePreset)}
        size="sm"
        radius="xl"
        allowDeselect={false}
      />
      {value.dateRange === 'custom' && (
        <DatePickerInput
          type="range"
          placeholder={t('agent_miner.delivery_impact.pick_dates')}
          value={[toDateOrNull(value.customStart), toDateOrNull(value.customEnd)]}
          onChange={(range) => {
            const [start, end] = range as DatesRangeValue;
            setCustomRange(toIsoDateOrNull(start), toIsoDateOrNull(end));
          }}
          valueFormatter={({ date }) => {
            if (!Array.isArray(date)) return '';
            const [start, end] = date;
            if (!start || !end) return '';
            return `${dayjs(start).format('MM/DD/YYYY')} – ${dayjs(end).format('MM/DD/YYYY')}`;
          }}
          maxDate={new Date()}
          size="sm"
          radius="xl"
          clearable
        />
      )}
      <Divider />
      <Group gap="xs">
        <Icon name="date_range" size={16} color={inkLight} />
        <Text weight="medium" size="small">
          {t('agent_miner.model_comparison.allocation_type')}
        </Text>
      </Group>
      <Select
        aria-label={t('agent_miner.model_comparison.allocation_type')}
        data={MODEL_COMPARISON_ALLOCATION_TYPES.map((type) => ({
          value: type,
          label: ALLOCATION_TYPE_LABELS[type],
        }))}
        value={effectiveAllocationType}
        onChange={(next) => {
          if (next) setAllocationType(next as TimeAllocationType);
        }}
        size="sm"
        radius="xl"
        allowDeselect={false}
      />
    </Stack>
  );

  return (
    <FilterPanel<typeof view>
      selectedOption={view}
      options={[
        {
          value: 'tiles',
          label: t('agent_miner.delivery_impact.view_tiles'),
          leftSection: <Icon name="dashboard" size={16} color={inkLight} />,
        },
        {
          value: 'trend',
          label: t('agent_miner.delivery_impact.view_trend'),
          leftSection: <Icon name="show_chart" size={16} color={inkLight} />,
        },
      ]}
      onOptionChange={setView}
      headerSection={dateSection}
      filterGroups={[]}
      onResetFilters={handleResetFilters}
      onClose={onClose}
    />
  );
};

/**
 * Main-section content for the Delivery Impact tab: the six AI-vs-baseline
 * comparison cards or the cycle-time trend chart, switched via the filter
 * panel's view selector. Owns the loading, error, and empty states.
 *
 * @returns {JSX.Element} The tiles or chart filling the main section
 */
export const DeliveryImpactMain = () => {
  const { t } = useTranslation();
  const params = useDeliveryImpactParams();
  const { view } = useDeliveryImpactView();
  const { data, query } = useDeliveryImpact(params);

  if (query.isError) {
    return (
      <CenteredState>
        <Icon name="error" color={inkLight} size={48} />
        <Text size="regular" color={inkLight}>
          {t('agent_miner.delivery_impact.error')}
        </Text>
      </CenteredState>
    );
  }

  if (query.isPending || !data) {
    return (
      <CenteredState>
        <Text size="regular" color={inkLight}>
          {t('agent_miner.delivery_impact.loading')}
        </Text>
      </CenteredState>
    );
  }

  if (isDeliveryImpactEmpty(data)) {
    return (
      <CenteredState>
        <Icon name="insights" color={inkLight} size={48} />
        <Text size="regular" color={inkLight}>
          {t('agent_miner.delivery_impact.empty')}
        </Text>
      </CenteredState>
    );
  }

  return (
    <Flex direction="column" gap={16} h="100%" data-testid="delivery-impact-main">
      {view === 'tiles' ? (
        <Stack gap={16}>
          <Stack gap={4}>
            <H3>{t('agent_miner.delivery_impact.tiles_title')}</H3>
            <Text size="small" color={inkLight}>
              {t('agent_miner.delivery_impact.tiles_subtitle')}
            </Text>
          </Stack>
          <ComparisonCards metrics={data.metrics} />
        </Stack>
      ) : (
        <ChartBox style={{ flex: 1, minHeight: 0 }}>
          <DeliveryTrendChart response={data} />
        </ChartBox>
      )}
    </Flex>
  );
};

/**
 * Side-section content for the Delivery Impact tab: the agentic sessions that
 * back the AI cohort (sessions with at least one linked work item), scoped to
 * the tab's window. Drills into a session on click and hands off to the full
 * Sessions view on expand.
 *
 * @returns {JSX.Element} The sessions side panel
 */
export const DeliveryImpactSessionsPanel = () => {
  const { t } = useTranslation();
  const params = useDeliveryImpactParams();
  const { setPanelOpen } = useDeliveryImpactDrilldown();
  const [searchParams, setSearchParams] = useSearchParams();
  const setActiveTab = useAgentMinerStore((state) => state.setActiveTab);

  const listParams = useMemo(
    () => ({
      organization_id: params.organization_id,
      page: 1,
      page_size: SESSIONS_PANEL_PAGE_SIZE,
      start_date: params.start_date,
      end_date: params.end_date,
      has_task: true,
      ordering: '-last_turn_at',
    }),
    [params.organization_id, params.start_date, params.end_date],
  );

  // Hand off to the Sessions tab with the task-linked filter applied (it shows
  // up as a removable pill there), dropping the Delivery Impact-only params so
  // the destination URL carries nothing the Sessions view can't act on.
  const handleExpand = () => {
    // Sessions is the ABSENT-`tab` state, but agent-miner.tsx resolves an
    // absent param by falling back to the STORED tab — which is still Delivery
    // Impact here. Without updating the store, deleting `tab` bounces straight
    // back to this tab (mirrors handleSelectTab in agent-miner.tsx).
    setActiveTab(AgentMinerTab.Sessions);
    const next = new URLSearchParams(searchParams);
    next.delete('tab');
    next.delete('di_view');
    next.delete('di_panel');
    next.delete('di_chart_view');
    next.delete('allocation');
    next.set('has_task', 'true');
    next.set('page', '1');
    setSearchParams(next, { replace: true });
  };

  return (
    <SessionListSideSection
      title={t('agent_miner.delivery_impact.ai_sessions_title')}
      subtitle={formatDateRange(params.start_date, params.end_date, {
        format: STANDARD_DATE_FORMATS.NUMERIC_DATE,
      })}
      listParams={listParams}
      onClose={() => setPanelOpen(false)}
      onExpand={handleExpand}
      emptyLabel={t('agent_miner.delivery_impact.ai_sessions_empty')}
    />
  );
};

/**
 * Centered icon-and-message block used for the loading, error, and empty
 * states.
 *
 * @param {{ children: React.ReactNode }} props - Component props
 * @returns {JSX.Element} The centered state block
 */
const CenteredState = ({ children }: { children: React.ReactNode }) => (
  <Flex direction="column" align="center" justify="center" gap={12} mih={280} h="100%">
    {children}
  </Flex>
);
